import type {
  CheckoutBoletoPaymentInstructions,
  CheckoutPaymentInstructions,
  CheckoutPixPaymentInstructions,
} from './checkout.types';

interface RawCheckoutPaymentInstructions {
  method?: string;
  externalPaymentId?: string;
  value?: number;
  dueDate?: string | null;
  dueDateTime?: string | null;
  qrCodeImageBase64?: string;
  copyPasteCode?: string;
  expirationDate?: string | null;
  identificationField?: string;
  barCode?: string | null;
  bankSlipUrl?: string | null;
}

function parsePixInstructions(raw: RawCheckoutPaymentInstructions): CheckoutPixPaymentInstructions | null {
  if (raw.method !== 'pix') {
    return null;
  }

  const copyPasteCode = typeof raw.copyPasteCode === 'string' ? raw.copyPasteCode.trim() : '';
  const qrCodeImageBase64 = typeof raw.qrCodeImageBase64 === 'string' ? raw.qrCodeImageBase64.trim() : '';

  if (!copyPasteCode && !qrCodeImageBase64) {
    return null;
  }

  return {
    method: 'pix',
    externalPaymentId: typeof raw.externalPaymentId === 'string' ? raw.externalPaymentId : '',
    value: typeof raw.value === 'number' ? raw.value : 0,
    dueDate: typeof raw.dueDate === 'string' ? raw.dueDate : null,
    dueDateTime: typeof raw.dueDateTime === 'string' ? raw.dueDateTime : null,
    qrCodeImageBase64,
    copyPasteCode,
    expirationDate: typeof raw.expirationDate === 'string' ? raw.expirationDate : null,
  };
}

function parseBoletoInstructions(raw: RawCheckoutPaymentInstructions): CheckoutBoletoPaymentInstructions | null {
  if (raw.method !== 'boleto') {
    return null;
  }

  const identificationField = typeof raw.identificationField === 'string' ? raw.identificationField.trim() : '';
  const barCode = typeof raw.barCode === 'string' ? raw.barCode.trim() : null;
  const bankSlipUrl = typeof raw.bankSlipUrl === 'string' ? raw.bankSlipUrl : null;

  if (!identificationField && !barCode && !bankSlipUrl) {
    return null;
  }

  return {
    method: 'boleto',
    externalPaymentId: typeof raw.externalPaymentId === 'string' ? raw.externalPaymentId : '',
    value: typeof raw.value === 'number' ? raw.value : 0,
    dueDate: typeof raw.dueDate === 'string' ? raw.dueDate : null,
    dueDateTime: typeof raw.dueDateTime === 'string' ? raw.dueDateTime : null,
    identificationField,
    barCode,
    bankSlipUrl,
  };
}

export function parseCheckoutPaymentInstructions(
  raw: unknown,
): CheckoutPaymentInstructions | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const instructions = raw as RawCheckoutPaymentInstructions;
  return parsePixInstructions(instructions) ?? parseBoletoInstructions(instructions);
}

export function getCheckoutSuccessMessage(
  paymentInstructions: CheckoutPaymentInstructions | null | undefined,
  paymentConfirmed = false,
  paymentMethod?: string | null,
): string {
  if (paymentConfirmed) {
    if (paymentMethod === 'credit_card') {
      return 'Pagamento no cartão confirmado! Sua contratação foi concluída com sucesso.';
    }
    if (paymentMethod === 'pix' || paymentInstructions?.method === 'pix') {
      return 'Pagamento PIX confirmado! Sua contratação foi concluída com sucesso.';
    }
    return 'Pagamento confirmado! Sua contratação foi concluída com sucesso.';
  }

  if (paymentMethod === 'credit_card' && !paymentInstructions) {
    return 'Sua contratação foi registrada com sucesso. O pagamento no cartão ainda está sendo processado.';
  }

  if (!paymentInstructions) {
    return 'Contratação registrada com sucesso. Em breve você receberá instruções de pagamento.';
  }

  if (paymentInstructions.method === 'pix') {
    return 'Contratação registrada! Use o QR Code ou o código PIX abaixo para concluir o pagamento.';
  }

  return 'Contratação registrada! Use o boleto abaixo para concluir o pagamento.';
}

export function getChargeCheckoutSuccessMessage(
  paymentInstructions: CheckoutPaymentInstructions | null | undefined,
  paymentConfirmed = false,
  paymentMethod?: string | null,
): string {
  if (paymentConfirmed) {
    if (paymentMethod === 'pix' || paymentInstructions?.method === 'pix') {
      return 'Pagamento PIX confirmado. Obrigada!';
    }
    if (paymentMethod === 'credit_card') {
      return 'Pagamento no cartão confirmado. Obrigada!';
    }
    return 'Pagamento confirmado. Obrigada!';
  }

  if (paymentMethod === 'credit_card' && !paymentInstructions) {
    return 'Obrigada! Recebemos o seu pagamento e ele está sendo processado.';
  }

  if (!paymentInstructions) {
    return 'Obrigada! Seu pagamento foi registrado e será processado em breve.';
  }

  if (paymentInstructions.method === 'pix') {
    return 'Quase lá! Conclua o PIX abaixo. Assim que o pagamento for confirmado, atualizamos automaticamente.';
  }

  return 'Quase lá! Use o boleto abaixo para concluir o pagamento.';
}
